import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { all } from 'rsvp';
import DS from 'ember-data';
import Route from 'cio/routes/base';
import FiltersService from 'cio/services/filters';
import MessageSettingsService from 'cio/services/message-settings';
import NewsletterModel from 'cio/models/newsletter';
import Transition from '@ember/routing/-private/transition';
import BroadcastsNewsletterSetupRecipientsIndexController from 'cio/pods/broadcasts/newsletter/setup/recipients/index/controller';
import ConfirmChangesService from 'cio/services/confirm-changes';

export default class IndexRoute extends Route {
  @service filters!: FiltersService;
  @service messageSettings!: MessageSettingsService;
  @service store!: DS.Store;
  @service confirmChanges!: ConfirmChangesService;

  model() {
    return this.store.findRecord(
      'newsletter',
      (this.paramsFor('broadcasts.newsletter') as Record<string, any>).newsletter_id,
    );
  }

  afterModel(model: NewsletterModel) {
    let recipients = model.findRecipients();
    let segments = this.store.findAll('short-segment');

    return all([
      recipients,
      segments,
      this.filters.process.perform(),
      this.messageSettings.loadMessagingLimit.perform(),
    ]);
  }

  setupController(
    controller: BroadcastsNewsletterSetupRecipientsIndexController,
    model: NewsletterModel,
    transition: Transition,
  ) {
    super.setupController(controller, model, transition);

    // current newsletter filters are set separately on the controller
    // Otherwise the following happens:
    // - newsletter search dropdown embedded in condition list component calls store.query('newsletter', ...);
    // - this updates all newsletters in Ember Data store
    // - this causes "model.filters" to be changed, causing unwanted re-render of condition list component
    controller.set('newsletterFilters', model.filters);
    // _sendRate is set separately on the controller, otherwise initialSendRate
    // is unchanged, and resetState uses the incorrect value
    controller.set(
      '_sendRate',
      isPresent(model.rateLimitEmailRate) || isPresent(model.rateLimitTimePeriod) ? 'limit' : 'asap',
    );
    controller.setProperties({
      initialSendRate: controller._sendRate,
      hasAcceptedRisksOfSendingToEveryone: model.sendToEveryone,
      originalSubscriptionTopicId: model.subscriptionTopicId,
      originalSendToUnsubscribed: model.sendToUnsubscribed,
    });
    controller.resetState();
    this.confirmChanges.register(controller, this);
    controller.fetchRecipients.perform();
  }

  resetController(
    controller: BroadcastsNewsletterSetupRecipientsIndexController,
    isExiting: boolean,
    transition: Transition,
  ) {
    super.resetController(controller, isExiting, transition);
    controller.setProperties({
      hasAcceptedRisksOfSendingToEveryone: false,
    });
  }
}
